<template>
  <el-col class="select-js select-primary">
    <multiselect
      v-if="resource"
      v-model="model"
      :options="remoteOptions"
      :open-direction="'bottom'"
      :group-values="groupValues"
      :group-select="groupSelect"
      :group-label="groupLabel"
      :loading="loading"
      :custom-label="customLabel || translated(itemName)"
      :multiple="multiple"
      close-on-select
      clear-on-select
      select-label=""
      selected-label=""
      deselect-label=""
      :placeholder="placeholder || $t('select.placeholder.text')"
      :label="itemName"
      :track-by="itemKey"
      :searchable="searchable"
      :internal-search="!remoteSearchEnabled"
      @search-change="remoteMethod"
      @input="onChange"
      @open="loadInitial"
    >
      <template slot="afterList">
        <infinite-loading
          v-if="!infiniteScrollBusy"
          @infinite="loadNextPage"
        />
      </template>

      <template
        v-if="$scopedSlots.singleLabel"
        slot="singleLabel"
        slot-scope="singleLabelProps"
      >
        <slot
          name="singleLabel"
          v-bind="singleLabelProps"
        />
      </template>
    </multiselect>

    <multiselect
      v-else
      v-model="model"
      :options="options"
      :group-values="groupValues"
      :group-select="groupSelect"
      :group-label="groupLabel"
      :searchable="searchable"
      :multiple="multiple"
      :custom-label="customLabel || translated(itemName)"
      close-on-select
      clear-on-select
      select-label=""
      selected-label=""
      deselect-label=""
      :placeholder="placeholder || $t('select.placeholder.text')"
      :label="itemName"
      :track-by="itemKey"
      @input="onChange"
    >
      <template
        v-if="$scopedSlots.singleLabel"
        slot="singleLabel"
        slot-scope="singleLabelProps"
      >
        <slot
          name="singleLabel"
          v-bind="singleLabelProps"
        />
      </template>
    </multiselect>
  </el-col>
</template>

<script>
  import Multiselect from 'vue-multiselect';
  import InfiniteLoading from 'vue-infinite-loading';
  import { AjaxDataProvider } from '@fps/vue-toolkit/dist/services/http/ajax-data-provider';
  import { dataGet } from '@fps/vue-toolkit/dist/util';
  import debounce from 'lodash-es/debounce';

  export default {
    components: {
      Multiselect,
      InfiniteLoading,
    },
    props: {
      // eslint-disable-next-line vue/require-prop-types
      value: {
        default: null,
      },
      itemName: {
        type: String,
        default: 'name',
      },
      itemKey: {
        type: String,
        default: 'id',
      },
      itemSearched: {
        type: String,
        default: 'name',
      },
      searchable: {
        type: Boolean,
        default: true,
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      options: {
        type: Array,
        default: () => [],
      },
      placeholder: {
        type: String,
        default: '',
      },
      groupValues: {
        type: String,
        default: '',
      },
      groupSelect: {
        type: Boolean,
        default: false,
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      groupLabel: {
        type: String,
        default: 'name',
      },
      customLabel: {
        type: String,
        default: null,
      },
      resource: {
        type: String,
        default: null,
      },
      remoteSearchEnabled: {
        type: Boolean,
        default: true,
      },
      resourceOptions: {
        type: Object,
        default: () => ({}),
      },
      autoload: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        model: '',
        loading: false,
        watchers: [],
        remoteOptions: [],
        dataProvider: null,
        initialLoad: false,
        infiniteScrollBusy: true,
        page: 0,
        nextPage: 1,
        remoteMethod: null,
        currentFilter: '',
        requestCache: {},
      };
    },
    created() {
      this.model = this.value;
      this.watchers.push(
        this.$watch('value', (value) => {
          this.model = value;
        }),
      );

      if (this.resource && this.resource.length) {
        this.dataProvider = new AjaxDataProvider(this.resource);
        this.dataProvider.filter(dataGet('filter', this.resourceOptions, []));

        this.remoteMethod = debounce(this.searchRemote.bind(this), 300);

        this.loadInitial();
      }
    },
    beforeDestroy() {
      this.watchers.forEach(item => item());
    },
    methods: {
      translated({ name, key }) {
        return (key && key !== undefined) ? this.$t(`select.primary.js.item.${key}`) : name;
      },
      onChange(value) {
        this.$emit('input', value);
        this.$emit('change', value);
      },
      searchRemote(value) {
        if (this.remoteSearchEnabled) {
          this.dataProvider.filter([
            ...dataGet('filter', this.resourceOptions, []),
            {
              property: this.itemSearched,
              operator: 'LIKE',
              value,
            }]);
          this.currentFilter = value;
          this.reload();
        }
      },
      load(replace) {
        this.loading = true;
        this.infiniteScrollBusy = true;
        if (
          this.requestCache[this.currentFilter] &&
          this.requestCache[this.currentFilter][this.page]
        ) {
          this.$nextTick(() => {
            this.loading = false;
            this.handleResponse(this.requestCache[this.currentFilter][this.page], replace);
          });
        } else {
          this.dataProvider
            .load(this.page)
            .then(({ data }) => {
              this.loading = false;
              this.handleResponse(data, replace);
            })
            .catch(() => {
              this.loading = false;
            });
        }
      },
      reload() {
        this.page = 1;
        this.nextPage = 1;
        this.load(true);
      },
      loadInitial() {
        if (!this.initialLoad) {
          this.initialLoad = true;
          this.load(true);
        }
      },
      loadNextPage() {
        if (this.page < this.nextPage) {
          this.page = this.nextPage;
          this.load();
        }
      },
      handleResponse(response, replace) {
        this.remoteOptions = replace ? response.data : this.remoteOptions.concat(response.data);
        this.page = response.current_page;
        this.nextPage = this.page < response.last_page ? this.page + 1 : null;

        if (response.data.length) {
          this.requestCache[this.currentFilter] = {
            ...this.requestCache[this.currentFilter],
            [this.page]: { ...response },
          };
        }

        if (this.nextPage) {
          this.infiniteScrollBusy = false;
        }
      },
    },
  };

</script>



// WEBPACK FOOTER //
// src/components/MainLeftFilters/SelectPrimaryJs.vue